import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/detailForm";
import { DetailBadge } from "@/features/ui/detail/DetailBadge";
import { DetailContent } from "@/features/ui/detail/DetailContent";
import { DetailField } from "@/features/ui/detail/DetailField";
import { DetailHeader } from "@/features/ui/detail/DetailHeader";
import { DetailInformTitle } from "@/features/ui/detail/DetailInformTitle";
import { DetailLabel } from "@/features/ui/detail/DetailLabel";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { DetailLinkWrapper } from "@/features/ui/detail/DetailLinkWrapper";
import { DetailTitle } from "@/features/ui/detail/DetailTitle";
import { useUserStore } from "@/store/userStore";
import { getUserCodeByUser } from "@/utils/checkData";
import { translateWithPartnerType } from "@/utils/partnerType";
import { purchaseOrderStatusColor, setPurchaseOrderStatus } from "@/utils/scmDataStatus";
import { Badge, Checkbox, Space } from "@mantine/core";
import { PurchaseOrdersGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const PurchaseOrderDetail = Object.assign(DetailHeader, {
  Title: DetailTitle,
  Inform: DetailInformTitle,
  Field: DetailField,
  Label: DetailLabel,
  Link: DetailLink,
  LinkWrapper: DetailLinkWrapper,
  Badge: DetailBadge,
  Content: DetailContent,
  CheckBox: Checkbox,
});

export interface PurchaseOrderDetailHeaderProps {
  data: PurchaseOrdersGet200ResponseRowsInner | undefined;
}

export const PurchaseOrderDetailHeader = (params: PurchaseOrderDetailHeaderProps) => {
  const { data } = params;
  const { openModal } = useModal();
  const userCode = getUserCodeByUser();
  const { t } = useTranslation();

  const { userInfo } = useUserStore();
  const partnerType = userInfo?.partner?.type;

  return (
    <>
      <PurchaseOrderDetail>
        <PurchaseOrderDetail.Title>
          {[`[${translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType)}]`]}{" "}
          {data?.code}
        </PurchaseOrderDetail.Title>
        <Space h={8} />
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>
            {translateWithPartnerType(t, "purchaseOrder.id", partnerType)}
          </PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>{data?.id}</PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>
            {translateWithPartnerType(t, "purchaseOrder.status", partnerType)}
          </PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>
            <Badge
              variant="filled"
              color={
                purchaseOrderStatusColor[data?.status as keyof typeof purchaseOrderStatusColor]
              }
            >
              {setPurchaseOrderStatus(data?.status ?? "")}
            </Badge>
          </PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
        {/* ㅋ테고라ㅣ */}
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>{t("purchaseOrderCategory.name")}</PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>
            {data?.purchaseOrderCategoryName}
          </PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>
            {translateWithPartnerType(t, "purchaseOrder.code", partnerType)}
          </PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>{data?.code}</PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>
            {translateWithPartnerType(t, "purchaseOrder.name", partnerType)}
          </PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>{data?.name}</PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>{t("partner.partner")}</PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>
            <PurchaseOrderDetail.Link
              onClick={() =>
                openModal(
                  <PartnerDetailForm partnerCode={data?.partnerCode} />,
                  null,
                  t("common.detail", { item: t("partner.partner") })
                )
              }
            >
              {data?.partner?.name}
            </PurchaseOrderDetail.Link>
          </PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>
            {translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType)}
          </PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>
            <PurchaseOrderDetail.Link
              onClick={() =>
                openModal(
                  <LocationDetailForm locationCode={data?.sourceLocationCode} />,
                  null,
                  t("common.detail", { item: t("location.location") })
                )
              }
            >
              {data?.sourceLocationName}
            </PurchaseOrderDetail.Link>
          </PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>{t("order.manager")}</PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>{data?.manager}</PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>
            {translateWithPartnerType(t, "purchaseOrder.orderDate", partnerType)}
          </PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>
            {dayjs(data?.orderDate).format("YYYY-MM-DD")}
          </PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>{t("order.createdAt")}</PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>
            {data?.creatorUserName} {dayjs(data?.createdAt).format("YYYY-MM-DD")}
          </PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>{t("order.deliveryDate")}</PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>
            {dayjs(data?.deliveryDeadline).format("YYYY-MM-DD")}
          </PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
        <PurchaseOrderDetail.Field>
          <PurchaseOrderDetail.Label>{t("info.updateInformation")}</PurchaseOrderDetail.Label>
          <PurchaseOrderDetail.Content>
            <PurchaseOrderDetail.Link>{data?.updaterUserName}</PurchaseOrderDetail.Link>
            {data?.updatedAt === null ? "" : dayjs(data?.updatedAt).format("YYYY-MM-DD")}
          </PurchaseOrderDetail.Content>
        </PurchaseOrderDetail.Field>
      </PurchaseOrderDetail>
    </>
  );
};
